/**
 * DPI metadata utilities for passport photo compliance
 * Adds 300 DPI EXIF metadata to JPEG images for professional printing
 */

import * as piexif from 'piexifjs';
import { debug } from './debug';

export interface DPIOptions {
  dpi?: number;
  software?: string;
}

const DEFAULT_OPTIONS: Required<DPIOptions> = {
  dpi: 300,
  software: 'Privacy-First Passport Photos'
};

/**
 * Converts a Blob to base64 string
 */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      
      // Check if it's a proper data URL
      if (!result.startsWith('data:image/jpeg;base64,')) {
        reject(new Error('Blob is not a JPEG image'));
        return;
      }
      
      // Remove data URL prefix (data:image/jpeg;base64,)
      const base64 = result.split(',')[1];
      debug.log(`✅ Base64 extraction complete, length: ${base64.length}`);
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Converts base64 string back to Blob with JPEG validation
 */
function base64ToBlob(base64: string, contentType: string): Blob {
  try {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    
    // Validate JPEG format one more time before creating blob
    if (byteArray.length >= 2) {
      debug.log(`🔍 Final JPEG check - first bytes: ${byteArray[0].toString(16).toUpperCase()} ${byteArray[1].toString(16).toUpperCase()}`);
      debug.log(`🔍 Final JPEG check - last bytes: ${byteArray[byteArray.length - 2].toString(16).toUpperCase()} ${byteArray[byteArray.length - 1].toString(16).toUpperCase()}`);
    }
    
    return new Blob([byteArray], { type: contentType || 'image/jpeg' });
    
  } catch (error) {
    debug.error('❌ base64ToBlob conversion failed:', error);
    throw new Error(`Failed to convert base64 to blob: ${error}`);
  }
}

/**
 * Adds DPI metadata to a JPEG image blob
 * 
 * @param imageBlob - The input JPEG image blob
 * @param options - DPI configuration options
 * @returns Promise<Blob> - New blob with DPI metadata
 */
export async function addDPIMetadata(
  imageBlob: Blob, 
  options: DPIOptions = {}
): Promise<Blob> {
  const finalOptions = { ...DEFAULT_OPTIONS, ...options };
  
  try {
    debug.log(`📏 Adding ${finalOptions.dpi} DPI metadata to image (${(imageBlob.size / 1024).toFixed(1)}KB)`);
    debug.log(`📝 Blob type: ${imageBlob.type}, size: ${imageBlob.size} bytes`);
    
    // Check if piexif is available
    if (!piexif || !piexif.ImageIFD || !piexif.dump || !piexif.insert) {
      throw new Error('piexifjs library not properly loaded');
    }
    debug.log('📦 piexifjs library loaded successfully');
    
    // Validate input blob is JPEG
    if (imageBlob.type !== 'image/jpeg') {
      throw new Error(`Expected JPEG blob, got: ${imageBlob.type || 'unknown type'}`);
    }
    
    // Convert blob to base64
    debug.log('🔄 Converting blob to base64...');
    const base64 = await blobToBase64(imageBlob);
    debug.log(`✅ Base64 conversion complete, length: ${base64.length}`);
    
    // Create EXIF dictionary with DPI information
    debug.log('📋 Creating EXIF dictionary...');
    const exifDict = {
      "0th": {
        [piexif.ImageIFD.XResolution]: [finalOptions.dpi, 1],
        [piexif.ImageIFD.YResolution]: [finalOptions.dpi, 1],
        [piexif.ImageIFD.ResolutionUnit]: 2, // 2 = inches, 3 = cm
        [piexif.ImageIFD.Software]: finalOptions.software,
        [piexif.ImageIFD.DateTime]: new Date().toISOString().replace(/T/, ' ').replace(/\..+/, '')
      }
    };
    debug.log('✅ EXIF dictionary created:', exifDict);
    
    // Generate EXIF bytes
    debug.log('🔧 Generating EXIF bytes...');
    const exifBytes = piexif.dump(exifDict);
    debug.log(`✅ EXIF bytes generated, length: ${exifBytes.length}`);
    
    // Insert EXIF into the JPEG using data URL format (the method that works)
    debug.log('💾 Inserting EXIF into JPEG...');
    const dataUrl = `data:image/jpeg;base64,${base64}`;
    const newDataUrl = piexif.insert(exifBytes, dataUrl);
    debug.log(`✅ EXIF inserted successfully using data URL method`);
    
    // Convert back to blob
    debug.log('🔄 Converting back to blob...');
    
    // Extract base64 from the returned data URL
    const finalBase64 = newDataUrl.startsWith('data:') 
      ? newDataUrl.split(',')[1] 
      : newDataUrl;
    
    const newBlob = base64ToBlob(finalBase64, imageBlob.type);
    
    const sizeDiff = newBlob.size - imageBlob.size;
    debug.log(`✅ DPI metadata added successfully (+${(sizeDiff / 1024).toFixed(1)}KB)`);
    debug.log(`📐 Resolution: ${finalOptions.dpi} DPI, Software: ${finalOptions.software}`);
    debug.log(`📊 Original size: ${(imageBlob.size / 1024).toFixed(1)}KB → New size: ${(newBlob.size / 1024).toFixed(1)}KB`);
    
    return newBlob;
    
  } catch (error) {
    debug.error('❌ Failed to add DPI metadata:', error);
    debug.warn('📷 Falling back to original image without DPI metadata');
    
    // Graceful degradation - return original blob if EXIF injection fails
    return imageBlob;
  }
}

/**
 * Extracts DPI information from an image blob (for verification)
 */
export async function getDPIFromImage(imageBlob: Blob): Promise<{ xResolution?: number; yResolution?: number; unit?: number } | null> {
  try {
    const base64 = await blobToBase64(imageBlob);
    const exifDict = piexif.load(base64) as any;
    
    const ifd0 = exifDict["0th"];
    if (!ifd0) {
      return null;
    }
    
    const xResolution = ifd0[piexif.ImageIFD.XResolution];
    const yResolution = ifd0[piexif.ImageIFD.YResolution];
    const unit = ifd0[piexif.ImageIFD.ResolutionUnit];
    
    return {
      xResolution: xResolution ? xResolution[0] / xResolution[1] : undefined,
      yResolution: yResolution ? yResolution[0] / yResolution[1] : undefined,
      unit
    };
  } catch (error) {
    debug.warn('⚠️ Could not extract DPI information:', error);
    return null;
  }
}

/**
 * Validates if an image has proper 300 DPI metadata
 */
export async function validateDPI(imageBlob: Blob, expectedDPI: number = 300): Promise<boolean> {
  const dpiInfo = await getDPIFromImage(imageBlob);
  
  if (!dpiInfo) {
    return false;
  }
  
  return dpiInfo.xResolution === expectedDPI && 
         dpiInfo.yResolution === expectedDPI && 
         dpiInfo.unit === 2; // inches
}

/**
 * Creates a DPI-enabled blob export function that can wrap existing canvas.toBlob calls
 */
export function createDPIExporter(dpi: number = 300) {
  return async (originalBlob: Blob): Promise<Blob> => {
    return addDPIMetadata(originalBlob, { dpi });
  };
}